/* eslint-disable */
//
// VoiceScoreButton.jsx
//
// A small, mobile-friendly microphone button that lets a user speak a
// player's Grass scores for a round. After listening it shows a large-font
// review popup where the parsed values can be edited or the voice input
// redone, before confirming and writing the values into the form.
//
// Props:
//   - playerName: string   (used only for display)
//   - onApply(values): called with { Unprotected, Protected, Penalties, Banker }
//                       when the user confirms.
//
import * as React from "react";
import { Button } from "@aws-amplify/ui-react";
import { Modal, Form, Row, Col, Spinner } from "react-bootstrap";
import { parseScores, roundToFive } from "../voiceParser";

// Resolve the browser SpeechRecognition implementation, if any.
function getSpeechRecognition() {
    if (typeof window === "undefined") return null;
    return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export default function VoiceScoreButton({ playerName, onApply }) {
    const recognitionRef = React.useRef(null);

    const [listening, setListening] = React.useState(false);
    const [showReview, setShowReview] = React.useState(false);
    const [transcript, setTranscript] = React.useState("");
    const [parsed, setParsed] = React.useState(null);

    // Editable values shown on the review popup.
    const [draft, setDraft] = React.useState({
        Unprotected: 0,
        Protected: 0,
        Penalties: 0,
        Banker: false,
    });

    const supported = !!getSpeechRecognition();

    // Clean up any in-flight recognition when unmounting.
    React.useEffect(() => {
        return () => {
            try {
                if (recognitionRef.current) recognitionRef.current.abort();
            } catch (e) {
                /* ignore */
            }
        };
    }, []);

    const applyParsedToDraft = (p) => {
        setDraft({
            Unprotected: p.Unprotected,
            Protected: p.Protected,
            Penalties: p.Penalties,
            Banker: p.Banker,
        });
    };

    const startListening = () => {
        const SR = getSpeechRecognition();
        if (!SR) {
            window.alert(
                "Voice input is not supported in this browser. On iPhone, Safari does not support speech recognition \u2014 please enter scores manually, or use Chrome on Android."
            );
            return;
        }

        let recognition;
        try {
            recognition = new SR();
        } catch (err) {
            window.alert("Could not start voice input: " + (err && err.message ? err.message : err));
            return;
        }

        recognition.lang = "en-GB";
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;
        recognition.continuous = false;

        recognition.onresult = (event) => {
            const text = event.results[0][0].transcript;
            setTranscript(text);
            const p = parseScores(text, { roundToMultipleOfFive: true });
            setParsed(p);
            applyParsedToDraft(p);
            setShowReview(true);
        };

        recognition.onerror = (event) => {
            setListening(false);
            const code = event && event.error ? event.error : "unknown";
            if (code === "no-speech") {
                window.alert("I didn't hear anything. Please tap the mic and try again.");
            } else if (code === "not-allowed" || code === "service-not-allowed") {
                window.alert(
                    "Microphone access was blocked. Please allow microphone permission for this site and try again."
                );
            } else if (code === "aborted") {
                /* user cancelled / unmount \u2014 stay quiet */
            } else {
                window.alert("Voice input error: " + code);
            }
        };

        recognition.onend = () => {
            setListening(false);
        };

        recognitionRef.current = recognition;
        try {
            recognition.start();
            setListening(true);
        } catch (err) {
            setListening(false);
            window.alert("Could not start listening: " + (err && err.message ? err.message : err));
        }
    };

    const stopListening = () => {
        try {
            if (recognitionRef.current) recognitionRef.current.stop();
        } catch (e) {
            /* ignore */
        }
        setListening(false);
    };

    const handleRedo = () => {
        setShowReview(false);
        // Small delay so the modal close doesn't fight with mic start on mobile.
        setTimeout(() => startListening(), 250);
    };

    const handleDraftNumberChange = (field, raw) => {
        const value = raw === "" ? 0 : parseInt(raw, 10);
        setDraft((d) => ({ ...d, [field]: isNaN(value) ? 0 : value }));
    };

    const handleConfirm = () => {
        // Enforce the multiple-of-5 rule on whatever the user finally entered.
        const finalValues = {
            Unprotected: roundToFive(Number(draft.Unprotected) || 0),
            Protected: roundToFive(Number(draft.Protected) || 0),
            Penalties: roundToFive(Number(draft.Penalties) || 0),
            Banker: !!draft.Banker,
        };
        setShowReview(false);
        if (typeof onApply === "function") onApply(finalValues);
    };

    const roundedNote =
        parsed && parsed.rounded && Object.keys(parsed.rounded).length > 0
            ? Object.entries(parsed.rounded)
                  .map(([f, orig]) => `${f}: heard ${orig}, rounded to ${roundToFive(orig)}`)
                  .join(" \u2022 ")
            : null;

    return (
        <>
            <Button
                type="button"
                size="small"
                variation="link"
                onClick={listening ? stopListening : startListening}
                aria-label={`Speak scores for ${playerName}`}
                style={{ paddingLeft: 8, paddingRight: 8 }}
            >
                {listening ? (
                    <>
                        <Spinner animation="grow" size="sm" role="status" aria-hidden="true" />{" "}
                        {"Listening\u2026 (tap to stop)"}
                    </>
                ) : (
                    <>{"\uD83C\uDFA4 Say scores"}</>
                )}
            </Button>

            <Modal
                show={showReview}
                onHide={() => setShowReview(false)}
                centered
                backdrop="static"
            >
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: "1.1rem" }}>
                        Confirm scores {playerName ? `\u2014 ${playerName}` : ""}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {transcript ? (
                        <p style={{ color: "#6c757d", fontStyle: "italic", marginBottom: 12 }}>
                            Heard: &ldquo;{transcript}&rdquo;
                        </p>
                    ) : null}

                    <Form>
                        <Form.Group as={Row} className="mb-3 align-items-center">
                            <Form.Label column xs={5} style={{ fontSize: "1.25rem" }}>
                                Unprotected
                            </Form.Label>
                            <Col xs={7}>
                                <Form.Control
                                    type="number"
                                    inputMode="numeric"
                                    step={5}
                                    value={draft.Unprotected}
                                    onChange={(e) => handleDraftNumberChange("Unprotected", e.target.value)}
                                    style={{ fontSize: "1.5rem", fontWeight: 600, textAlign: "right" }}
                                />
                            </Col>
                        </Form.Group>

                        <Form.Group as={Row} className="mb-3 align-items-center">
                            <Form.Label column xs={5} style={{ fontSize: "1.25rem" }}>
                                Protected
                            </Form.Label>
                            <Col xs={7}>
                                <Form.Control
                                    type="number"
                                    inputMode="numeric"
                                    step={5}
                                    value={draft.Protected}
                                    onChange={(e) => handleDraftNumberChange("Protected", e.target.value)}
                                    style={{ fontSize: "1.5rem", fontWeight: 600, textAlign: "right" }}
                                />
                            </Col>
                        </Form.Group>

                        <Form.Group as={Row} className="mb-3 align-items-center">
                            <Form.Label column xs={5} style={{ fontSize: "1.25rem" }}>
                                Penalties
                            </Form.Label>
                            <Col xs={7}>
                                <Form.Control
                                    type="number"
                                    inputMode="numeric"
                                    step={5}
                                    value={draft.Penalties}
                                    onChange={(e) => handleDraftNumberChange("Penalties", e.target.value)}
                                    style={{ fontSize: "1.5rem", fontWeight: 600, textAlign: "right" }}
                                />
                            </Col>
                        </Form.Group>

                        <Form.Group as={Row} className="mb-2 align-items-center">
                            <Form.Label column xs={5} style={{ fontSize: "1.25rem" }}>
                                Banker
                            </Form.Label>
                            <Col xs={7}>
                                <Form.Check
                                    type="switch"
                                    id={`voice-banker-${playerName}`}
                                    label={draft.Banker ? "Yes" : "No"}
                                    checked={draft.Banker}
                                    onChange={(e) => setDraft((d) => ({ ...d, Banker: e.target.checked }))}
                                    style={{ fontSize: "1.25rem" }}
                                />
                            </Col>
                        </Form.Group>
                    </Form>

                    {roundedNote ? (
                        <p style={{ color: "#b8860b", fontSize: "0.9rem", marginTop: 8 }}>
                            {roundedNote}
                        </p>
                    ) : null}
                </Modal.Body>
                <Modal.Footer>
                    <Button type="button" variation="link" onClick={handleRedo}>
                        {"\uD83C\uDFA4 Redo voice"}
                    </Button>
                    <Button type="button" variation="primary" onClick={handleConfirm}>
                        Confirm
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
